import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Plus, Pencil, Trash2, Save, X, Building2, Loader2, Upload, Image as ImageIcon, Star, ChevronDown, FileText, Download, Filter, Sparkles, GripVertical } from 'lucide-react';
import { doc, updateDoc, getDoc, writeBatch } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { runIsometricPipeline } from '../../lib/aiRender';
import { useApartments } from '../../lib/hooks/useApartments';
import { useBuildingConfig } from '../../lib/hooks/useBuildingConfig';
import { EXPOSURES, getExposureText } from '../../lib/exposure';
import Input from '../../components/Input';

interface ApartmentFormData {
  number: string;
  entrance: string;
  floor: number;
  secondaryFloor?: number | null;
  type: string;
  rooms: number;
  area: number;
  netArea?: number;
  price: number;
  promoPrice?: number;
  status: 'available' | 'reserved' | 'sold';
  description: string;
  features: string[];
  exposure: string;
  internalNotes?: string;
}

const initialFormData: ApartmentFormData = {
  number: '',
  entrance: '1',
  floor: 1,
  secondaryFloor: null,
  type: 'studio',
  rooms: 1,
  area: 0,
  netArea: 0,
  price: 0,
  promoPrice: undefined,
  status: 'available',
  description: '',
  features: [],
  exposure: 'south',
  internalNotes: ''
};

const ApartmentsEditor = () => {
  const { config: buildingConfig, getAvailableFloors, getEntranceLabel, getApartmentTypeLabel } = useBuildingConfig();
  const { 
    apartments, 
    loading, 
    error, 
    addApartment, 
    updateApartment, 
    deleteApartment,
    addImages,
    deleteImage,
    setMainImage,
    uploadProgress,
    uploadBrochure,
    deleteBrochure,
    refreshApartments
  } = useApartments();
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<ApartmentFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  // A row only becomes draggable while the pointer is held on its handle, so
  // dragging elsewhere in the row still selects text normally.
  const [dragArmedId, setDragArmedId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [isGeneratingIso, setIsGeneratingIso] = useState(false);
  const [isoStage, setIsoStage] = useState('');
  const [isCopyingIso, setIsCopyingIso] = useState(false);
  
  const [filters, setFilters] = useState({
    entrance: 'all',
    floor: 'all',
    type: 'all',
    status: 'all'
  });

  const isMaisonette = (type: string) => {
    return type.includes('maisonette');
  };

  const handleTypeChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      type: value,
      secondaryFloor: isMaisonette(value) ? (prev.floor + 1) : null
    }));
  };

  const handleInputChange = (field: keyof ApartmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // The AI isometric render is always the aiInteriors entry whose file name
  // starts with iso_ (generated or copied); other entries are regular photos.
  const isoRenderOf = (apt: any): string | undefined =>
    (apt?.model3d?.aiInteriors || []).find((u: string) => u.includes('iso_'));

  // Reuses a finished render from an identical apartment on another floor —
  // copies the file into this apartment's folder, so no AI generation is spent.
  const handleCopyIsometric = async (targetId: string, sourceId: string) => {
    const source = apartments.find((a) => a.id === sourceId);
    const sourceUrl = source && isoRenderOf(source);
    if (!sourceUrl) return;
    const target = apartments.find((a) => a.id === targetId);
    if (
      target &&
      isoRenderOf(target) &&
      !window.confirm('Този обект вече има 3D изглед. Да го заменя ли с копието?')
    ) {
      return;
    }
    setIsCopyingIso(true);
    try {
      const res = await fetch(sourceUrl);
      if (!res.ok) throw new Error('Снимката на другия обект не може да се зареди');
      const blob = await res.blob();
      const r = storageRef(storage, `apartments/${targetId}/iso_${Date.now()}.png`);
      await uploadBytes(r, blob, { contentType: blob.type || 'image/png' });
      const url = await getDownloadURL(r);
      const snap = await getDoc(doc(db, 'apartments', targetId));
      const existing: string[] = snap.data()?.model3d?.aiInteriors || [];
      const rest = existing.filter((u) => !u.includes('iso_'));
      await updateDoc(doc(db, 'apartments', targetId), { 'model3d.aiInteriors': [url, ...rest] });
      await refreshApartments();
      alert(`3D изгледът е копиран от „${source!.number}" — без нова генерация.`);
    } catch (error: any) {
      console.error('Error copying isometric render:', error);
      alert(error.message || 'Грешка при копиране на 3D изгледа');
    } finally {
      setIsCopyingIso(false);
    }
  };

  // AI isometric render from the apartment's main sketch; stored first in the
  // "AI интериор" gallery (model3d.aiInteriors) on the public page.
  const handleGenerateIsometric = async (apartmentId: string) => {
    const apt = apartments.find((a) => a.id === apartmentId);
    const sketch = apt?.mainImage || apt?.images?.[0];
    if (!sketch) {
      alert('Първо качете скица (план) на обекта и я задайте като главна снимка.');
      return;
    }
    setIsGeneratingIso(true);
    try {
      const { blob, model } = await runIsometricPipeline(sketch, 'apartment', setIsoStage);
      const r = storageRef(storage, `apartments/${apartmentId}/iso_${Date.now()}.png`);
      await uploadBytes(r, blob, { contentType: blob.type || 'image/png' });
      const url = await getDownloadURL(r);
      const snap = await getDoc(doc(db, 'apartments', apartmentId));
      const existing: string[] = snap.data()?.model3d?.aiInteriors || [];
      // Replace any previous isometric render (kept first in the gallery).
      const rest = existing.filter((u) => !u.includes(`/apartments%2F${apartmentId}%2Fiso_`) && !u.includes(`/apartments/${apartmentId}/iso_`));
      await updateDoc(doc(db, 'apartments', apartmentId), { 'model3d.aiInteriors': [url, ...rest] });
      alert(
        model === 'pro'
          ? '3D изгледът е генериран успешно (Pro модел). Ще се покаже в секция „AI интериор" на страницата на обекта.'
          : '3D изгледът е генериран с резервния Flash модел (по-ниска точност) — пробвайте отново за Pro качество.'
      );
    } catch (error: any) {
      console.error('Error generating isometric render:', error);
      alert(error.message || 'Грешка при генериране на 3D изгледа');
    } finally {
      setIsGeneratingIso(false);
      setIsoStage('');
    }
  };

  const handleSave = async (id: string | null = null) => {
    try {
      setIsSaving(true);
      if (id) {
        await updateApartment(id, formData);
      } else {
        await addApartment(formData);
      }
      setEditingId(null);
      setIsAdding(false);
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error saving apartment:', error);
      alert('Грешка при запазване на апартамента');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Сигурни ли сте, че искате да изтриете този апартамент?')) {
      try {
        await deleteApartment(id);
      } catch (error) {
        console.error('Error deleting apartment:', error);
        alert('Грешка при изтриване на апартамента');
      }
    }
  };

  // Releasing the button anywhere disarms the row: without this, a press on
  // the handle that ends elsewhere would leave the whole row draggable.
  React.useEffect(() => {
    if (!dragArmedId) return;
    const disarm = () => setDragArmedId(null);
    window.addEventListener('mouseup', disarm);
    return () => window.removeEventListener('mouseup', disarm);
  }, [dragArmedId]);

  // Reordering works off the FULL list, not the filtered view, so dragging a
  // row while a filter is active still lands it in the right global position.
  const handleRowDrop = async (targetId: string) => {
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }
    const from = apartments.findIndex((a) => a.id === draggedId);
    const to = apartments.findIndex((a) => a.id === targetId);
    setDraggedId(null);
    if (from === -1 || to === -1) return;

    const reordered = [...apartments];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    setIsReordering(true);
    try {
      // Firestore caps a batch at 500 writes, so commit in chunks.
      for (let start = 0; start < reordered.length; start += 400) {
        const batch = writeBatch(db);
        reordered.slice(start, start + 400).forEach((apartment, offset) => {
          batch.update(doc(db, 'apartments', apartment.id), { order: start + offset });
        });
        await batch.commit();
      }
      await refreshApartments();
    } catch (error) {
      console.error('Error reordering apartments:', error);
      alert('Грешка при запазване на подредбата');
    } finally {
      setIsReordering(false);
    }
  };

  const handleEdit = (apartment: any) => {
    setEditingId(apartment.id);
    setFormData({
      number: apartment.number,
      entrance: apartment.entrance,
      floor: apartment.floor,
      secondaryFloor: apartment.secondaryFloor,
      type: apartment.type,
      rooms: apartment.rooms,
      area: apartment.area,
      netArea: apartment.netArea || 0,
      price: apartment.price,
      promoPrice: apartment.promoPrice,
      status: apartment.status,
      description: apartment.description || '',
      features: apartment.features || [],
      exposure: apartment.exposure || 'south',
      internalNotes: apartment.internalNotes || ''
    });
  };

  // Escape closes the form, and the page behind it must not scroll while the
  // modal is open. Deliberately no backdrop click — losing a half-filled form
  // to a stray click is worse than one extra click on „Отказ".
  const isFormOpen = isAdding || !!editingId;
  React.useEffect(() => {
    if (!isFormOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isFormOpen]);

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(initialFormData);
    setShowImageUpload(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp']
    },
    multiple: false,
    onDrop: async (acceptedFiles) => {
      if (!editingId) return;
      
      try {
        await addImages(editingId, acceptedFiles);
      } catch (error) {
        console.error('Error uploading images:', error);
        alert('Грешка при качване на снимките');
      }
    }
  });

  const handleDeleteImage = async (apartmentId: string, imageUrl: string) => {
    if (window.confirm('Сигурни ли сте, че искате да изтриете тази снимка?')) {
      try {
        await deleteImage(apartmentId, imageUrl);
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Грешка при изтриване на снимката');
      }
    }
  };

  const handleSetMainImage = async (apartmentId: string, imageUrl: string) => {
    try {
      await setMainImage(apartmentId, imageUrl);
    } catch (error) {
      console.error('Error setting main image:', error);
      alert('Грешка при задаване на главна снимка');
    }
  };

  const filteredApartments = apartments.filter(apartment => {
    return (
      (filters.entrance === 'all' || apartment.entrance === filters.entrance) &&
      (filters.floor === 'all' || apartment.floor === parseInt(filters.floor)) &&
      (filters.type === 'all' || apartment.type === filters.type) &&
      (filters.status === 'all' || apartment.status === filters.status)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Building2 className="w-8 h-8 text-primary mr-3" />
          <h1 className="text-2xl font-bold">Управление на апартаменти</h1>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Добави апартамент
        </button>
      </div>

      {(
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Филтри</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Вход
              </label>
              <select
                value={filters.entrance}
                onChange={(e) => setFilters(prev => ({ ...prev, entrance: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">Всички</option>
                {buildingConfig.entrances.map((entrance) => (
                  <option key={entrance.id} value={entrance.id}>
                    {entrance.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Етаж
              </label>
              <select
                value={filters.floor}
                onChange={(e) => setFilters(prev => ({ ...prev, floor: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">Всички</option>
                {getAvailableFloors().map((floor) => (
                  <option key={floor} value={floor}>Етаж {floor}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Тип
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">Всички</option>
                {buildingConfig.apartmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Статус
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">Статус</option>
                <option value="available">Свободен</option>
                <option value="reserved">Резервиран</option>
                <option value="sold">Продаден</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* The form opens over the list instead of above it: editing a unit far
          down the table used to scroll the page to the top and back again. */}
      {(isAdding || editingId) && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-semibold">
                {editingId ? formData.number || 'Редакция на обект' : 'Нов обект'}
              </h2>
              <button
                onClick={handleCancel}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Затвори"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Име / Номер на обекта
              </label>
              <input
                type="text"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="напр. Апартамент 14 или Офис 3"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Показва се като заглавие на обекта. Напишете пълното име, напр. „Апартамент 14" или „Офис 3".
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Вход
              </label>
              <select
                value={formData.entrance}
                onChange={(e) => handleInputChange('entrance', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {buildingConfig.entrances.map((entrance) => (
                  <option key={entrance.id} value={entrance.id}>
                    {entrance.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Тип
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {buildingConfig.apartmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Етаж
              </label>
              <select
                value={formData.floor}
                onChange={(e) => {
                  const newFloor = Number(e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    floor: newFloor,
                    secondaryFloor: isMaisonette(prev.type) ? newFloor + 1 : null
                  }));
                }}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {getAvailableFloors(formData.entrance).map((floor) => (
                  <option key={floor} value={floor}>Етаж {floor}</option>
                ))}
              </select>
            </div>

            {isMaisonette(formData.type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Втори етаж
                </label>
                <select
                  value={formData.secondaryFloor || ''}
                  onChange={(e) => setFormData({ ...formData, secondaryFloor: Number(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  {getAvailableFloors(formData.entrance).map((floor) => (
                    <option key={floor} value={floor}>Етаж {floor}</option>
                  ))}
                </select>
              </div>
            )}

            <Input
              label="Брой стаи"
              type="number"
              min="1"
              value={formData.rooms}
              onChange={(e) => handleInputChange('rooms', Number(e.target.value))}
              required
            />

            <Input
              label="Чиста площ (кв.м)"
              type="number"
              min="0"
              step="0.01"
              value={formData.netArea || 0}
              onChange={(e) => handleInputChange('netArea', Number(e.target.value))}
            />

            <Input
              label="Обща площ (кв.м)"
              type="number"
              min="0"
              step="0.01"
              value={formData.area}
              onChange={(e) => handleInputChange('area', Number(e.target.value))}
              required
            />

            <Input
              label="Цена (EUR)"
              type="number"
              min="0"
              value={formData.price}
              onChange={(e) => handleInputChange('price', Number(e.target.value))}
              required
            />

            <Input
              label="Промоционална цена (EUR)"
              type="number"
              min="0"
              value={formData.promoPrice || ''}
              onChange={(e) => handleInputChange('promoPrice', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Остави празно ако няма промоция"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Статус
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value as 'available' | 'reserved' | 'sold')}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="available">Свободен</option>
                <option value="reserved">Резервиран</option>
                <option value="sold">Продаден</option>
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Изложение
              </label>
              <select
                value={formData.exposure}
                onChange={(e) => handleInputChange('exposure', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {EXPOSURES.map((exposure) => (
                  <option key={exposure.value} value={exposure.value}>
                    {exposure.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Описание на апартамента..."
            />
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Вътрешни бележки
            </label>
            <textarea
              value={formData.internalNotes || ''}
              onChange={(e) => handleInputChange('internalNotes', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Вътрешни бележки (не се показват на клиентите)..."
            />
          </div>

          {editingId && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Снимки
                </label>
                <div className="flex items-center gap-2">
                  {apartments.some((a) => a.id !== editingId && isoRenderOf(a)) && (
                    <select
                      value=""
                      disabled={isGeneratingIso || isCopyingIso}
                      onChange={(e) => {
                        if (e.target.value) handleCopyIsometric(editingId!, e.target.value);
                      }}
                      title="Копира готовия 3D изглед от обект със същата планировка на друг етаж — без нова AI генерация"
                      className="px-3 py-2 border rounded-lg text-sm text-gray-700 bg-white hover:border-primary transition-colors disabled:opacity-50"
                    >
                      <option value="">
                        {isCopyingIso ? 'Копирам...' : 'Копирай 3D от...'}
                      </option>
                      {apartments
                        .filter((a) => a.id !== editingId && isoRenderOf(a))
                        .map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.number} (ет. {a.floor})
                          </option>
                        ))}
                    </select>
                  )}
                  <button
                    onClick={() => handleGenerateIsometric(editingId!)}
                    disabled={isGeneratingIso}
                    title="Генерира фотореалистичен 3D изглед от скицата с AI"
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-secondary to-secondary-dark text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {isGeneratingIso ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5 mr-2" />
                    )}
                    {isGeneratingIso ? (isoStage || 'Генерирам...') : '3D изглед (AI)'}
                  </button>
                  <button
                    onClick={() => setShowImageUpload(!showImageUpload)}
                    className="flex items-center px-4 py-2 text-primary hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <ImageIcon className="w-5 h-5 mr-2" />
                    {showImageUpload ? 'Скрий качването' : 'Качи снимки'}
                  </button>
                </div>
              </div>

              {showImageUpload && (
                <div {...getRootProps()} className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors mb-6">
                  <input {...getInputProps()} />
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {isDragActive
                      ? 'Пуснете снимките тук...'
                      : 'Плъзнете снимки тук или кликнете, за да изберете'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Поддържани формати: PNG, JPG, JPEG, WEBP
                  </p>
                </div>
              )}

              {editingId && apartments.find(apt => apt.id === editingId)?.images && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {apartments.find(apt => apt.id === editingId)?.images.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Апартамент ${formData.number} - ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleSetMainImage(editingId, imageUrl)}
                          className={`p-2 rounded-full ${
                            apartments.find(apt => apt.id === editingId)?.mainImage === imageUrl
                              ? 'bg-yellow-500 text-white'
                              : 'bg-white text-gray-800'
                          } hover:bg-yellow-500 hover:text-white transition-colors`}
                          title={apartments.find(apt => apt.id === editingId)?.mainImage === imageUrl ? 'Главна снимка' : 'Задай като главна'}
                        >
                          <Star className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteImage(editingId, imageUrl)}
                          className="p-2 bg-white text-red-600 rounded-full hover:bg-red-600 hover:text-white transition-colors"
                          title="Изтрий"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      {uploadProgress[imageUrl] !== undefined && uploadProgress[imageUrl] < 100 && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="text-white">
                            {uploadProgress[imageUrl]}%
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Брошура (PDF)
            </label>
            <div className="flex items-center space-x-4">
              {editingId && apartments.find(apt => apt.id === editingId)?.brochureUrl ? (
                <div className="flex items-center justify-between w-full bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Брошура</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <a
                      href={apartments.find(apt => apt.id === editingId)?.brochureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-primary hover:text-primary-dark transition-colors"
                      title="Изтегли"
                    >
                      <Download className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => {
                        if (editingId && apartments.find(apt => apt.id === editingId)?.brochureUrl) {
                          if (window.confirm('Сигурни ли сте, че искате да изтриете брошурата?')) {
                            deleteBrochure(editingId, apartments.find(apt => apt.id === editingId)!.brochureUrl!);
                          }
                        }
                      }}
                      className="p-2 text-red-600 hover:text-red-900 transition-colors"
                      title="Изтрий"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex-1">
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file && editingId) {
                        try {
                          await uploadBrochure(editingId, file);
                          await refreshApartments();
                        } catch (error) {
                          console.error('Error uploading brochure:', error);
                          alert('Error uploading brochure');
                        }
                      }
                    }}
                  />
                  <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    <Upload className="w-6 h-6 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      Качете PDF брошура
                    </span>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Характеристики
            </label>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => {
                      const newFeatures = [...formData.features];
                      newFeatures[index] = e.target.value;
                      handleInputChange('features', newFeatures);
                    }}
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <button
                    onClick={() => {
                      const newFeatures = formData.features.filter((_, i) => i !== index);
                      handleInputChange('features', newFeatures);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => handleInputChange('features', [...formData.features, ''])}
                className="flex items-center px-4 py-2 text-primary hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Добави характеристика
              </button>
            </div>
          </div>

            </div>

            <div className="flex justify-end space-x-4 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={handleCancel}
                className="px-6 py-2 border rounded-lg bg-white hover:bg-gray-100 transition-colors"
              >
                Отказ
              </button>
              <button
                onClick={() => handleSave(editingId)}
                disabled={isSaving}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Запазване...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Запази
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
        <div className="min-w-[1400px]">
          {/* border-separate: sticky cells are unreliable inside a table with
              the default border-collapse. */}
          <table className="w-full border-separate border-spacing-0">
            {/* Row borders live on the cells: with border-separate the browser
                does not paint borders on <tr>, so divide-y would vanish. */}
            <thead className="bg-gray-50 [&>tr>th]:border-b [&>tr>th]:border-gray-200">
              <tr>
                <th className="w-10 px-2 py-3" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Вход
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Апартамент
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Етаж
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тип
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Изложение
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Чиста площ
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Обща площ
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Цена
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                {/* Pinned to the right edge so the actions stay reachable on
                    narrow screens; the other columns scroll under it. */}
                <th className="sticky right-0 z-20 bg-gray-50 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-6px_0_6px_-4px_rgba(0,0,0,0.15)]">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white [&>tr>td]:border-b [&>tr>td]:border-gray-200">
              {filteredApartments.map((apartment) => (
                <tr
                  key={apartment.id}
                  draggable={dragArmedId === apartment.id && !isReordering}
                  onDragStart={() => setDraggedId(apartment.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleRowDrop(apartment.id)}
                  onDragEnd={() => {
                    setDraggedId(null);
                    setDragArmedId(null);
                  }}
                  className={`group transition-colors ${
                    draggedId === apartment.id ? 'opacity-40' : 'hover:bg-gray-50'
                  }`}
                >
                  <td
                    className="w-10 px-2 py-3 text-gray-400 cursor-move"
                    title="Влачете, за да преместите реда"
                    onMouseDown={() => setDragArmedId(apartment.id)}
                    onMouseUp={() => setDragArmedId(null)}
                  >
                    <GripVertical className="w-5 h-5" />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">{getEntranceLabel(apartment.entrance)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">{apartment.number}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {apartment.floor}
                    {apartment.secondaryFloor && ` - ${apartment.secondaryFloor}`}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {getApartmentTypeLabel(apartment.type)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {getExposureText(apartment.exposure)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {apartment.netArea ? `${apartment.netArea} кв.м` : '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">{apartment.area} кв.м</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {apartment.price > 0 ? `€${apartment.price.toLocaleString()}` : ''}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      apartment.status === 'available'
                        ? 'bg-green-100 text-green-800'
                        : apartment.status === 'reserved'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {apartment.status === 'available'
                        ? 'Свободен'
                        : apartment.status === 'reserved'
                        ? 'Резервиран'
                        : 'Продаден'}
                    </span>
                  </td>
                  <td className="sticky right-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-3 whitespace-nowrap text-right text-sm shadow-[-6px_0_6px_-4px_rgba(0,0,0,0.15)]">
                    <button
                      onClick={() => handleEdit(apartment)}
                      className="text-primary hover:text-primary-dark mr-3 transition-colors"
                      title="Редактирай"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(apartment.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="Изтрий"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApartmentsEditor;